import { parseEntryDate, sumJmd } from './finances';
import { filterByPeriod } from './period';
import { isOverdue } from './todos';
import { poTotalJmd } from './purchaseOrders';
import { isOverdue as invoiceOverdue, invoiceTotalJmd } from './invoices';
import {
  formatMonth,
  sellThrough,
  statementTotal,
  statusOfStockist,
  totalsOf,
  unpaidDue,
} from './stockist';
import { todayISO } from './dates';

// Everything the Overview shows is derived here, with no Supabase and no
// clock reads — `today`/`now` are always passed in. That keeps the page a
// renderer and makes the arithmetic testable.

export const REVENUE_GOAL_JMD = 1000000;

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

// The period immediately before this one, of the same length, so "vs last
// month" stays honest when the selector isn't on a month.
export function previousPeriod(period) {
  if (!period?.start || !period?.end) return null;
  const spanDays = Math.round((period.end - period.start) / 86400000) + 1;
  const end = new Date(period.start.getFullYear(), period.start.getMonth(), period.start.getDate() - 1);
  const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - spanDays + 1);
  return { start, end };
}

export function moneySummary(income, expenses, period) {
  const prev = previousPeriod(period);
  const inNow = sumJmd(filterByPeriod(income, period));
  const exNow = sumJmd(filterByPeriod(expenses, period));
  const inPrev = prev ? sumJmd(filterByPeriod(income, prev)) : null;
  const exPrev = prev ? sumJmd(filterByPeriod(expenses, prev)) : null;

  return {
    income: inNow,
    expenses: exNow,
    net: inNow - exNow,
    previous:
      prev === null
        ? null
        : { income: inPrev, expenses: exPrev, net: inPrev - exPrev },
  };
}

// Direction is reported separately from the number, because "up" is good for
// income and bad for expenses — the card decides how to colour it.
export function changeVs(current, previous) {
  if (previous === null || previous === undefined) return null;
  return { previous, delta: current - previous, up: current >= previous };
}

export function goalProgress(revenueThisYear, goal = REVENUE_GOAL_JMD) {
  const pct = goal > 0 ? (revenueThisYear / goal) * 100 : 0;
  return {
    revenue: revenueThisYear,
    goal,
    // Clamped for the bar only; the printed percentage stays truthful so
    // beating the goal reads as beating it.
    barPct: Math.max(0, Math.min(100, pct)),
    pct,
  };
}

export function revenueThisYear(income, today) {
  const year = today.getFullYear();
  return sumJmd(income.filter((e) => parseEntryDate(e.entry_date).getFullYear() === year));
}

// ---------------------------------------------------------------------------
// Needs attention — actionable items, not counts
// ---------------------------------------------------------------------------

const DUE_SOON_DAYS = 7;

function withinDays(dateStr, today, days) {
  if (!dateStr) return false;
  const due = parseEntryDate(dateStr);
  const limit = new Date(today.getFullYear(), today.getMonth(), today.getDate() + days);
  return due <= limit;
}

export function buildAttention({ todos = [], purchaseOrders = [], marketingAssets = [] }, today) {
  const openTodos = todos.filter((t) => t.status === 'Open');
  // Overdue first, then soonest due, then undated — the order you'd act in.
  const rankedTodos = [...openTodos].sort((a, b) => {
    const ao = isOverdue(a) ? 0 : 1;
    const bo = isOverdue(b) ? 0 : 1;
    if (ao !== bo) return ao - bo;
    if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
    return a.due_date ? -1 : b.due_date ? 1 : 0;
  });

  const duePOs = purchaseOrders
    .filter(
      (po) =>
        (po.status === 'Ordered' || po.status === 'Shipped') &&
        withinDays(po.expected_delivery_date, today, DUE_SOON_DAYS)
    )
    .sort((a, b) => (a.expected_delivery_date || '').localeCompare(b.expected_delivery_date || ''));

  const inReview = marketingAssets.filter((a) => a.status === 'In Review');

  return {
    todos: { total: openTodos.length, items: rankedTodos.slice(0, 3) },
    purchaseOrders: { total: duePOs.length, items: duePOs.slice(0, 3) },
    marketing: { total: inReview.length, items: inReview.slice(0, 3) },
  };
}

// ---------------------------------------------------------------------------
// Invoices on the Overview
// ---------------------------------------------------------------------------

// Money you are owed, kept strictly separate from money you have.
//
// The three money cards above report what actually moved. This is the other
// half of the picture and it is *not* the same thing, so it never joins them:
// `due` is sold stock awaiting payment plus unpaid direct invoices — a real
// claim on someone — while `stockOut` is inventory sitting in shops, which is
// neither earned nor owed. Adding those two together is exactly the mistake
// the Invoices page was redesigned to avoid, so they stay separate here too.
export function moneyOwed(invoices = [], statementsByInvoice = {}, today = todayISO()) {
  let due = 0;
  let stockOut = 0;
  let stockOutUnits = 0;

  for (const invoice of invoices) {
    if (invoice.kind === 'Stockist') {
      const statements = statementsByInvoice[invoice.id] || [];
      due += unpaidDue(statements);
      const status = statusOfStockist(invoice, statements, today);
      if (status !== 'Completed' && status !== 'Cancelled') {
        const totals = totalsOf(sellThrough(invoice, statements));
        stockOut += totals.remainingValue;
        stockOutUnits += totals.remaining;
      }
    } else if (invoice.status === 'Sent') {
      // Issued and unpaid. A Draft is not a claim on anyone yet.
      due += invoiceTotalJmd(invoice, invoice.invoice_items);
    }
  }

  return { due, stockOut, stockOutUnits };
}

// The invoice items worth acting on, ranked by how much they cost you to
// ignore: money already earned but unpaid first, then months the shop hasn't
// reported yet — you can't invoice for sales nobody has told you about.
//
// Items, not a count, like every other attention row.
export function invoiceAttention(invoices = [], statementsByInvoice = {}, today = todayISO()) {
  const items = [];

  for (const invoice of invoices) {
    if (invoice.kind === 'Stockist') {
      const statements = statementsByInvoice[invoice.id] || [];

      for (const statement of statements) {
        if (statement.status === 'Paid') continue;
        const amount = statementTotal(statement);
        if (amount <= 0) continue;
        items.push({
          id: statement.id,
          invoiceId: invoice.id,
          rank: 0,
          sortKey: statement.period_month,
          amount,
          label: invoice.bill_to_name,
          meta: `${formatMonth(statement.period_month)} sales unpaid`,
        });
      }

      if (statusOfStockist(invoice, statements, today) === 'Awaiting monthly sales') {
        items.push({
          id: `${invoice.id}-report`,
          invoiceId: invoice.id,
          rank: 1,
          sortKey: invoice.issue_date,
          amount: null,
          label: invoice.bill_to_name,
          meta: 'Monthly sales not reported yet',
        });
      }
    } else if (invoice.status === 'Sent') {
      const overdue = invoiceOverdue(invoice, today);
      items.push({
        id: invoice.id,
        invoiceId: invoice.id,
        // An overdue invoice outranks an unreported month; one merely awaiting
        // payment does not.
        rank: overdue ? 0 : 2,
        sortKey: invoice.due_date || invoice.issue_date,
        amount: invoiceTotalJmd(invoice, invoice.invoice_items),
        label: invoice.bill_to_name,
        meta: overdue ? `Overdue since ${invoice.due_date}` : `Invoice ${invoice.invoice_number} unpaid`,
      });
    }
  }

  items.sort((a, b) => a.rank - b.rank || (a.sortKey || '').localeCompare(b.sortKey || ''));
  return { total: items.length, items: items.slice(0, 3) };
}

// ---------------------------------------------------------------------------
// Recent activity — assembled from the timestamps records already carry.
// There is no activity/audit table, so nothing here is invented: each event
// is a real row with a real `created_at`/`completed_at`.
// ---------------------------------------------------------------------------

export const ACTIVITY_TONES = {
  task: 'task',
  batch: 'positive',
  po: 'po',
  income: 'positive',
  expense: 'neutral',
  marketing: 'marketing',
  // Stock going out on consignment is its own kind of event — not money in,
  // not a purchase. Money coming *back* from an invoice is tagged `income`,
  // because that's what it is.
  invoice: 'invoice',
};

export function buildActivity(
  {
    todos = [],
    batches = [],
    purchaseOrders = [],
    income = [],
    expenses = [],
    invoices = [],
    statementsByInvoice = {},
  },
  limit = 8
) {
  const events = [];
  const push = (at, kind, label, detail) => {
    if (!at) return;
    const when = new Date(at);
    if (Number.isNaN(when.getTime())) return;
    events.push({ at: when, kind, tone: ACTIVITY_TONES[kind], label, detail });
  };

  todos
    .filter((t) => t.status === 'Done' && t.completed_at)
    .forEach((t) => push(t.completed_at, 'task', 'Task completed', t.title));

  batches.forEach((b) =>
    push(
      b.created_at,
      'batch',
      'Batch logged',
      [b.formula?.family?.name || b.formula?.product_name, b.batch_code].filter(Boolean).join(' · ')
    )
  );

  purchaseOrders.forEach((po) =>
    push(po.created_at, 'po', `${po.po_number} raised`, po.vendor?.name || null)
  );

  income.forEach((e) => push(e.created_at, 'income', 'Income recorded', e.description || null));
  expenses.forEach((e) => push(e.created_at, 'expense', 'Expense recorded', e.description || null));

  invoices.forEach((invoice) => {
    if (invoice.kind === 'Stockist') {
      const units = totalsOf(sellThrough(invoice, [])).sent;
      push(
        invoice.created_at,
        'invoice',
        'Stock sent to stockist',
        `${invoice.bill_to_name} · ${units} units`
      );
      // Each month's payment is its own event: that's the thing that actually
      // brought money in, and one consignment produces many of them.
      for (const statement of statementsByInvoice[invoice.id] || []) {
        if (statement.status !== 'Paid') continue;
        push(
          statement.paid_on || statement.created_at,
          'income',
          'Stockist payment received',
          `${invoice.bill_to_name} · ${formatMonth(statement.period_month)}`
        );
      }
    } else {
      push(
        invoice.created_at,
        'invoice',
        `Invoice ${invoice.invoice_number} raised`,
        invoice.bill_to_name
      );
      if (invoice.status === 'Paid') {
        push(invoice.paid_at, 'income', 'Invoice paid', invoice.bill_to_name);
      }
    }
  });

  return events.sort((a, b) => b.at - a.at).slice(0, limit);
}

// Groups into Today / Yesterday / an actual date, which is how you'd read it.
export function groupActivityByDay(events, now) {
  const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const today = dayKey(now);
  const yesterday = dayKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));

  const groups = [];
  for (const event of events) {
    const key = dayKey(event.at);
    const label =
      key === today
        ? 'Today'
        : key === yesterday
          ? 'Yesterday'
          : event.at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const existing = groups.find((g) => g.key === key);
    if (existing) existing.events.push(event);
    else groups.push({ key, label, events: [event] });
  }
  return groups;
}

export function formatTime(date) {
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

// ---------------------------------------------------------------------------

export function latestBatch(batches) {
  return (
    [...batches].sort((a, b) => (b.made_on || '').localeCompare(a.made_on || ''))[0] || null
  );
}

export function batchUnits(batch) {
  if (!batch) return null;
  if (Array.isArray(batch.yield_breakdown) && batch.yield_breakdown.length) {
    return batch.yield_breakdown.reduce((sum, y) => sum + Number(y.count || 0), 0);
  }
  return batch.actual_yield_units ?? null;
}

export function openOrderValue(purchaseOrders) {
  return purchaseOrders
    .filter((po) => po.status === 'Ordered' || po.status === 'Shipped')
    .reduce((sum, po) => sum + poTotalJmd(po.purchase_order_items || [], po.exchange_rate), 0);
}
